import {form200Refine, form200Schema} from '@/components/Forms/Schemas/SchemaForm200.tsx';
import {form201Schema} from '@/components/Forms/Schemas/SchemaForm201.tsx';
import {form202Refine, form202Schema} from '@/components/Forms/Schemas/SchemaForm202.tsx';
import {form203Refine, form203Schema} from '@/components/Forms/Schemas/SchemaForm203.tsx';
import {form204Schema} from '@/components/Forms/Schemas/SchemaForm204.tsx';
import {form205Schema} from '@/components/Forms/Schemas/SchemaForm205.tsx';
import {form206Schema} from '@/components/Forms/Schemas/SchemaForm206.tsx';
import {form270Schema} from '@/components/Forms/Schemas/SchemaForm270.tsx';
import {form280Schema} from '@/components/Forms/Schemas/SchemaForm280.tsx';
import {form300Refine, form300Schema} from '@/components/Forms/Schemas/SchemaForm300.tsx';
import {form400Refine, form400Schema} from '@/components/Forms/Schemas/SchemaForm400.tsx';
import {form500Refine, form500Schema} from '@/components/Forms/Schemas/SchemaForm500.tsx';
import {form501Refine, form501Schema} from '@/components/Forms/Schemas/SchemaForm501.tsx';
import {form600Schema} from '@/components/Forms/Schemas/SchemaForm600.tsx';
import {form700Schema} from '@/components/Forms/Schemas/SchemaForm700.tsx';
import type {TFunction} from 'i18next';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

const form100Schema = z.object({});

export type Form200SchemaType = ReturnType<typeof form200Schema>;
type Form201SchemaType = ReturnType<typeof form201Schema>;
type Form202SchemaType = ReturnType<typeof form202Schema>;
type Form203SchemaType = ReturnType<typeof form203Schema>;
type Form204SchemaType = ReturnType<typeof form204Schema>;
type Form205SchemaType = ReturnType<typeof form205Schema>;
type Form206SchemaType = ReturnType<typeof form206Schema>;
type Form270SchemaType = ReturnType<typeof form270Schema>;
type Form280SchemaType = ReturnType<typeof form280Schema>;
type Form300SchemaType = ReturnType<typeof form300Schema>;
type Form400SchemaType = ReturnType<typeof form400Schema>;
type Form500SchemaType = ReturnType<typeof form500Schema>;
type Form501SchemaType = ReturnType<typeof form501Schema>;
type Form600SchemaType = ReturnType<typeof form600Schema>;
type Form700SchemaType = ReturnType<typeof form700Schema>;

export type FormSchemas =
    | Form200SchemaType
    | Form201SchemaType
    | Form202SchemaType
    | Form203SchemaType
    | Form204SchemaType
    | Form205SchemaType
    | Form206SchemaType
    | Form270SchemaType
    | Form280SchemaType
    | Form300SchemaType
    | Form400SchemaType
    | Form500SchemaType
    | Form501SchemaType
    | Form600SchemaType
    | Form700SchemaType
    | typeof form100Schema;

export type RefinedForms =
    | typeof form100Schema
    | ReturnType<typeof form200Refine>
    | ReturnType<typeof form201Schema>
    | ReturnType<typeof form202Refine>
    | ReturnType<typeof form203Refine>
    | ReturnType<typeof form204Schema>
    | ReturnType<typeof form205Schema>
    | ReturnType<typeof form206Schema>
    | ReturnType<typeof form270Schema>
    | ReturnType<typeof form280Schema>
    | ReturnType<typeof form300Refine>
    | ReturnType<typeof form400Refine>
    | ReturnType<typeof form500Refine>
    | ReturnType<typeof form501Refine>
    | ReturnType<typeof form600Schema>
    | ReturnType<typeof form700Schema>
    | FullSchema;

const fullFormSchema = (t: TFunction<'translation', undefined>) => {
    const schema = form100Schema
        .merge(form200Schema(t))
        .merge(form201Schema(t))
        .merge(form202Schema(t))
        .merge(form203Schema(t))
        .merge(form204Schema(t));
    const schemab = form205Schema(t)
        .merge(form206Schema(t))
        .merge(form270Schema(t))
        .merge(form280Schema(t))
        .merge(form300Schema(t))
        .merge(form400Schema(t));
    const schmeac = form500Schema(t).merge(form600Schema(t)).merge(form700Schema());

    return schema.merge(schemab).merge(schmeac);
};

export type FullSchema = ReturnType<typeof fullFormSchema>;

const useFormSchema = () => {
    const {t} = useTranslation();

    const fullSchema = useMemo<FullSchema>(() => {
        return fullFormSchema(t);
    }, [t]);

    const getFormSchema = useCallback(
        (userStep?: number): RefinedForms => {
            switch (userStep) {
                case 100:
                    return z.object({});
                case 200:
                    return form200Refine(form200Schema(t), t);
                case 201:
                    return form201Schema(t);
                case 202:
                    return form202Refine(form202Schema(t), t);
                case 203:
                    return form203Refine(form203Schema(t), t);
                case 204:
                    return form204Schema(t);
                case 205:
                    return form205Schema(t);
                case 206:
                    return form206Schema(t);
                case 270:
                    return form270Schema(t);
                case 280:
                    return form280Schema(t);
                case 300:
                    return form300Refine(form300Schema(t), t);
                case 400:
                    return form400Refine(form400Schema(t), t);
                case 500:
                    return form500Refine(form500Schema(t), t);
                case 501:
                    return form501Refine(form501Schema(t), t);
                case 600:
                    return form600Schema(t);
                case 700:
                    return form700Schema();
            }

            return fullSchema;
        },
        [t],
    );

    return {getFormSchema, fullSchema};
};

export default useFormSchema;
